import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronLeft, Cpu, Edit, FileText } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { rotuloAreaAtivo, rotuloStatusAtivo } from '@/lib/i18n';
import { PainelDocumentos } from '@/components/maquinas/painel-documentos';

interface ApreciacaoVinculada {
  id: string;
  numero: string;
  titulo: string;
  status?: string;
  revisao?: number;
  ativoId?: string | null;
}

interface Maquina {
  id: string;
  area?: string | null;
  local?: string | null;
  item?: string | null;
  idItem?: string | null;
  equipamento?: string | null;
  tipo?: string | null;
  template?: string | null;
  funcionario?: string | null;
  tag?: string | null;
  status?: string | null;
  observacao?: string | null;
  refSap?: string | null;
  refSismanaAntigo?: string | null;
  refSismanaNovo?: string | null;
  numeroAmt?: string | null;
  numeroAprPraxis?: string | null;
  dataLevantamento?: string | null;
  previsaoLevantamento?: string | null;
  apreciacoes?: ApreciacaoVinculada[];
}

/**
 * Ficha da máquina: dados do equipamento, acervo documental e apreciações.
 *
 * As apreciações vêm por caminhos diferentes conforme a porta de entrada: quem
 * chega pela lista ou pelo dashboard traz a linha inteira em `location.state`
 * (a listagem de ativos já inclui as apreciações), e quem abre a URL direto cai
 * na busca por `listarAtivos` — `GET /ativos/:id` devolve só as colunas do
 * ativo, sem o relacionamento.
 */
export function MaquinaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t, formatarData } = useI18n();
  const { projeto } = useProjeto();

  const maquinaDaNavegacao = (location.state as { maquina?: Maquina } | null)?.maquina;
  const [maquina, setMaquina] = useState<Maquina | null>(
    maquinaDaNavegacao && maquinaDaNavegacao.id === id ? maquinaDaNavegacao : null
  );
  const [apreciacoes, setApreciacoes] = useState<ApreciacaoVinculada[]>(
    maquinaDaNavegacao?.id === id ? maquinaDaNavegacao?.apreciacoes ?? [] : []
  );
  const [loading, setLoading] = useState(!maquina);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelado = false;

    const carregar = async () => {
      try {
        const resposta = await apiService.buscarAtivo(id);
        if (cancelado) return;
        if (resposta.success && resposta.data) {
          setMaquina((anterior) => ({ ...(anterior ?? {}), ...resposta.data }));
        } else {
          setErro(t('maquinas.form.erro.naoEncontrada'));
        }
      } catch (error) {
        console.error('Erro ao carregar máquina:', error);
        if (!cancelado) setErro(t('maquinas.form.erro.carregar'));
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    carregar();
    return () => { cancelado = true; };
  }, [id, t]);

  // Apreciações para quem entrou pela URL direta (sem a linha da listagem).
  useEffect(() => {
    if (!id || !projeto || !maquina || apreciacoes.length > 0) return;
    const chave = maquina.tag || maquina.idItem || maquina.item || maquina.equipamento;
    if (!chave) return;

    let cancelado = false;
    const buscarApreciacoes = async () => {
      try {
        const resposta = await apiService.listarAtivos({
          projetoId: projeto.id,
          busca: chave,
          limit: 100,
        });
        if (cancelado || !resposta.success || !Array.isArray(resposta.data)) return;
        const encontrado = resposta.data.find((item: Maquina) => item.id === id);
        if (encontrado?.apreciacoes?.length) setApreciacoes(encontrado.apreciacoes);
      } catch (error) {
        console.warn('Não foi possível carregar as apreciações da máquina:', error);
      }
    };

    buscarApreciacoes();
    return () => { cancelado = true; };
  }, [id, projeto, maquina, apreciacoes.length]);

  if (loading && !maquina) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue mx-auto mb-3" />
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('maquinas.detalhe.carregando')}
          </p>
        </div>
      </div>
    );
  }

  if (!maquina) {
    return (
      <div className="space-y-4">
        <Link
          to="/maquinas"
          className="inline-flex items-center text-sm font-semibold text-schmersal-blue dark:text-white hover:underline"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('maquinas.detalhe.voltar')}
        </Link>
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4">
          {erro || t('maquinas.form.erro.naoEncontrada')}
        </p>
      </div>
    );
  }

  const semValor = t('maquinas.detalhe.dados.semValor');
  const campos: Array<{ rotulo: string; valor: string }> = [
    { rotulo: t('comum.rotulo.tipo'), valor: maquina.tipo || semValor },
    { rotulo: t('maquinas.form.campo.tag'), valor: maquina.tag || semValor },
    { rotulo: t('maquinas.form.campo.item'), valor: maquina.item || semValor },
    { rotulo: t('maquinas.form.campo.idItem'), valor: maquina.idItem || semValor },
    { rotulo: t('comum.rotulo.area'), valor: maquina.area ? rotuloAreaAtivo(t, maquina.area) : semValor },
    { rotulo: t('comum.rotulo.local'), valor: maquina.local || semValor },
    { rotulo: t('maquinas.form.campo.funcionario'), valor: maquina.funcionario || semValor },
    { rotulo: t('maquinas.form.campo.template'), valor: maquina.template || semValor },
    { rotulo: t('maquinas.form.campo.numeroAprPraxis'), valor: maquina.numeroAprPraxis || semValor },
    { rotulo: t('maquinas.form.campo.numeroAmt'), valor: maquina.numeroAmt || semValor },
    { rotulo: t('maquinas.form.campo.refSap'), valor: maquina.refSap || semValor },
    { rotulo: t('maquinas.form.campo.refSismanaNovo'), valor: maquina.refSismanaNovo || semValor },
    { rotulo: t('maquinas.form.campo.refSismanaAntigo'), valor: maquina.refSismanaAntigo || semValor },
    {
      rotulo: t('maquinas.detalhe.dados.levantamento'),
      valor: maquina.dataLevantamento ? formatarData(maquina.dataLevantamento) : semValor,
    },
    {
      rotulo: t('maquinas.detalhe.dados.previsaoLevantamento'),
      valor: maquina.previsaoLevantamento ? formatarData(maquina.previsaoLevantamento) : semValor,
    },
  ];

  const nome = maquina.equipamento || t('maquinas.detalhe.semNome');

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div>
        <Link
          to="/maquinas"
          className="inline-flex items-center text-sm font-semibold text-schmersal-blue dark:text-white hover:underline"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('maquinas.detalhe.voltar')}
        </Link>

        <div className="mt-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex-shrink-0 h-11 w-11 rounded-lg bg-schmersal-blue/10 dark:bg-schmersal-blue/25 flex items-center justify-center">
              <Cpu className="h-6 w-6 text-schmersal-blue dark:text-schmersal-blue-light" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white truncate">
                {nome}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {maquina.area && <span>{rotuloAreaAtivo(t, maquina.area)}</span>}
                {maquina.local && <span>· {maquina.local}</span>}
                {maquina.tag && <span>· {maquina.tag}</span>}
                {maquina.status && (
                  <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-schmersal-yellow/20 text-schmersal-blue dark:text-schmersal-yellow">
                    {rotuloStatusAtivo(t, maquina.status)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Link
            to={`/maquinas/${maquina.id}/editar`}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-schmersal-blue rounded-lg
              shadow-md text-sm font-semibold text-schmersal-blue dark:text-white bg-white dark:bg-schmersal-gray-darker
              hover:bg-schmersal-blue hover:text-white transition-all duration-200 w-full md:w-auto"
          >
            <Edit className="h-4 w-4 mr-2" />
            {t('maquinas.lista.acao.editar')}
          </Link>
        </div>
      </div>

      {/* Documentos da máquina */}
      <PainelDocumentos maquinaId={maquina.id} nomeMaquina={nome} />

      {/* Dados do equipamento */}
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
        <div className="px-6 py-4 border-b border-rg-line dark:border-schmersal-gray-dark">
          <h2 className="text-lg font-bold text-schmersal-gray-dark dark:text-white">
            {t('maquinas.detalhe.dados.titulo')}
          </h2>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 px-6 py-5">
          {campos.map((campo) => (
            <div key={campo.rotulo}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {campo.rotulo}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-schmersal-gray-dark dark:text-white break-words">
                {campo.valor}
              </dd>
            </div>
          ))}
        </dl>
        {maquina.observacao && (
          <div className="px-6 pb-5">
            <dt className="text-xs font-semibold uppercase tracking-wide text-schmersal-gray-medium dark:text-schmersal-gray-light">
              {t('comum.rotulo.observacoes')}
            </dt>
            <dd className="mt-0.5 text-sm text-schmersal-gray-dark dark:text-white whitespace-pre-line">
              {maquina.observacao}
            </dd>
          </div>
        )}
      </div>

      {/* Apreciações vinculadas */}
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
        <div className="px-6 py-4 border-b border-rg-line dark:border-schmersal-gray-dark">
          <h2 className="text-lg font-bold text-schmersal-gray-dark dark:text-white">
            {t('maquinas.detalhe.apreciacoes.titulo')}
          </h2>
        </div>
        {apreciacoes.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('maquinas.detalhe.apreciacoes.vazio')}
          </p>
        ) : (
          <ul className="divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
            {apreciacoes.map((apreciacao) => (
              <li key={apreciacao.id}>
                <Link
                  to={`/apreciacoes/${apreciacao.id}`}
                  title={t('maquinas.detalhe.apreciacoes.abrir')}
                  className="flex items-center gap-3 px-6 py-4 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-darkest transition-colors duration-150"
                >
                  <FileText className="h-4 w-4 flex-none text-schmersal-blue dark:text-schmersal-blue-light" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-schmersal-gray-dark dark:text-white truncate">
                      {apreciacao.numero}
                      {typeof apreciacao.revisao === 'number' && (
                        <span className="ml-2 text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">
                          {t('maquinas.detalhe.apreciacoes.revisao', { revisao: apreciacao.revisao })}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate">
                      {apreciacao.titulo}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
